import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import AllInclusiveRoundedIcon  from '@mui/icons-material/AllInclusiveRounded';
import AutoAwesomeRoundedIcon   from '@mui/icons-material/AutoAwesomeRounded';
import BoltRoundedIcon          from '@mui/icons-material/BoltRounded';
import CheckRoundedIcon         from '@mui/icons-material/CheckRounded';
import DevicesRoundedIcon       from '@mui/icons-material/DevicesRounded';
import ExpandMoreRoundedIcon    from '@mui/icons-material/ExpandMoreRounded';
import FlashOnRoundedIcon       from '@mui/icons-material/FlashOnRounded';
// import FormatQuoteRoundedIcon   from '@mui/icons-material/FormatQuoteRounded';
import LockOutlinedIcon         from '@mui/icons-material/LockOutlined';
import SyncRoundedIcon          from '@mui/icons-material/SyncRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';

import LogoSVG from '../../assets/icons/LogoSVG';
import {
  trackHeroCTAClick,
  trackSeePricingClick,
  trackNavGetStartedClick,
  trackNavSignInClick,
  trackLandingPlanClick,
  trackFAQExpand,
  trackLandingEngagement,
} from '../../utils/analytics';

import './styles.scss';

// ── Constants ────────────────────────────────────────────────────────────────

const SITE_URL = 'https://notecore.app';

// ── JSON-LD structured data ───────────────────────────────────────────────────

const STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebApplication',
      name: 'NoteCORE',
      url: SITE_URL,
      description:
        'NoteCORE is the intelligent writing workspace with a rich-text block editor, automatic sync, and a one-time Pro plan. Free forever — no ads, no tracking.',
      applicationCategory: 'ProductivityApplication',
      operatingSystem: 'Web',
      offers: [
        {
          '@type': 'Offer',
          name: 'Basic',
          price: '0',
          priceCurrency: 'INR',
          description: 'Free forever. Up to 3 notes with full rich-text block editor.',
        },
        {
          '@type': 'Offer',
          name: 'Pro',
          price: '199',
          priceCurrency: 'INR',
          description: 'One-time payment for unlimited notes and lifetime access.',
        },
      ],
    },
    {
      '@type': 'Organization',
      name: 'NoteCORE',
      url: SITE_URL,
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: "What's included in the Basic plan?",
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The Basic plan gives you up to 3 notes with the full rich-text block editor and all content block types. No credit card required ever.',
          },
        },
        {
          '@type': 'Question',
          name: 'What does "lifetime" mean for Pro?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'One payment, yours forever. You pay ₹199 once and get unlimited notes and all future updates — no recurring charges.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I upgrade from Basic to Pro later?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely. You can upgrade at any time from within the app. There are no recurring charges — just one payment.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is my data safe?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Your notes are encrypted at rest and in transit. We never sell your data.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I get a refund?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'All purchases are final. We do not offer refunds after payment is completed.',
          },
        },
      ],
    },
  ],
};

// ── FAQ data ──────────────────────────────────────────────────────────────────

const FAQS = [
  {
    q: "What's included in the Basic plan?",
    a: 'The Basic plan gives you up to 3 notes with the full rich-text block editor and all content block types. No credit card required — ever.',
  },
  {
    q: 'What does "lifetime" mean for Pro?',
    a: 'One payment, yours forever. You pay ₹199 once and get unlimited notes and all future updates — no recurring charges.',
  },
  {
    q: 'Can I upgrade from Basic to Pro later?',
    a: 'Absolutely. You can upgrade at any time from within the app. There are no recurring charges — just one payment.',
  },
  {
    q: 'What payment methods are accepted?',
    a: 'All major credit and debit cards, UPI, and net banking — processed securely via Razorpay.',
  },
  {
    q: 'Is my data safe?',
    a: 'Yes. Your notes are encrypted at rest and in transit. We never sell your data.',
  },
  {
    q: 'Can I get a refund?',
    a: 'All purchases are final. We do not offer refunds after payment is completed. Please review the plan details carefully before purchasing.',
  },
];

// ── Floating orb canvas ───────────────────────────────────────────────────────

const OrbCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const COLORS = ['#8b5cf6', '#38bdf8', '#14b8a6', '#6366f1', '#a78bfa', '#0ea5e9', '#7c3aed', '#2dd4bf'];

    const orbs = Array.from({ length: 8 }, (_, i) => ({
      x:  (canvas.width  / 8) * i + Math.random() * (canvas.width  / 8),
      y:  Math.random() * canvas.height,
      r:  200 + Math.random() * 260,
      vx: (Math.random() - 0.5) * 1.4,
      vy: (Math.random() - 0.5) * 1.4,
      color: COLORS[i % COLORS.length],
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      orbs.forEach((o) => {
        o.x += o.vx;
        o.y += o.vy;
        if (o.x < -o.r) o.x = canvas.width  + o.r;
        if (o.x > canvas.width  + o.r) o.x = -o.r;
        if (o.y < -o.r) o.y = canvas.height + o.r;
        if (o.y > canvas.height + o.r) o.y = -o.r;

        const g = ctx.createRadialGradient(o.x, o.y, 0, o.x, o.y, o.r);
        g.addColorStop(0, o.color + '28');
        g.addColorStop(1, o.color + '00');
        ctx.beginPath();
        ctx.arc(o.x, o.y, o.r, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animId);
    };
  }, []);

  return <canvas ref={canvasRef} className="landing-canvas" aria-hidden="true" />;
};

// ── Feature card ──────────────────────────────────────────────────────────────

const FeatureCard = ({ icon, title, description }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Box className="landing-feature-card">
      <Box className="landing-feature-card__icon" aria-hidden="true">{icon}</Box>
      <Typography
        variant="h6"
        component="h3"
        fontWeight={700}
        sx={{ color: '#f0f0f0', mb: 0.75, fontSize: '1rem', lineHeight: 1.4 }}
      >
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.52)', lineHeight: 1.7 }}>
        {description}
      </Typography>
    </Box>
  </Grid>
);

// ── Main component ────────────────────────────────────────────────────────────

const LandingPage = () => {
  const navigate        = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [expandedFaq, setExpandedFaq] = useState(false);

  // Logged-in users go straight to their dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Track engagement: fire once if user is still on page after 30 s
  useEffect(() => {
    const timer = setTimeout(() => trackLandingEngagement(30), 30000);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>NoteCORE — Focused Writing Workspace | Free Note-Taking App</title>
        <meta
          name="description"
          content="NoteCORE is the intelligent writing workspace with a rich-text block editor, automatic sync, and a one-time Pro plan at ₹199. Free forever — no ads, no tracking."
        />
        <link rel="canonical" href={`${SITE_URL}/`} />
        <meta property="og:type"        content="website" />
        <meta property="og:title"       content="NoteCORE — The Focused Writing Workspace" />
        <meta property="og:description" content="NoteCORE keeps your notes organised, searchable, and always with you. Free forever plan available." />
        <meta property="og:url"         content={`${SITE_URL}/`} />
        <meta property="og:image"       content={`${SITE_URL}/og.png`} />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content="NoteCORE — The Focused Writing Workspace" />
        <meta name="twitter:description" content="NoteCORE keeps your notes organised, searchable, and always with you." />
        <meta name="twitter:image"       content={`${SITE_URL}/og.png`} />
        <script type="application/ld+json">{JSON.stringify(STRUCTURED_DATA)}</script>
      </Helmet>

      <Box className="landing-root">
        <OrbCanvas />

        {/* ── Navigation ─────────────────────────────────────────────────── */}
        <Box component="header" className="landing-header">
          <Box className="landing-header__inner">
            <Box
              className="landing-header__logo"
              onClick={() => navigate('/')}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/')}
              aria-label="NoteCORE home"
            >
              <LogoSVG height="30px" />
            </Box>

            <Box className="landing-header__actions">
              <Button
                variant="text"
                onClick={() => { trackNavSignInClick(); navigate('/auth'); }}
                sx={{
                  color: 'rgba(255,255,255,0.68)',
                  fontWeight: 600,
                  fontSize: 14,
                  textTransform: 'none',
                  borderRadius: '10px',
                  '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.07)' },
                }}
              >
                Sign in
              </Button>
              <Button
                variant="contained"
                onClick={() => { trackNavGetStartedClick(); navigate('/auth'); }}
                sx={{
                  background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
                  borderRadius: '12px',
                  fontWeight: 700,
                  fontSize: 14,
                  textTransform: 'none',
                  px: 2.5,
                  py: 1,
                  boxShadow: '0 4px 20px rgba(124,58,237,0.42)',
                  '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #7c3aed)' },
                }}
              >
                Get started free
              </Button>
            </Box>
          </Box>
        </Box>

        {/* ── Main content ────────────────────────────────────────────────── */}
        <Box component="main">

          {/* ── Hero ──────────────────────────────────────────────────────── */}
          <Container maxWidth="lg">
            <Box component="section" className="landing-hero" aria-label="Hero">
              <Chip
                icon={<AutoAwesomeRoundedIcon sx={{ fontSize: '12px !important', color: '#c4b5fd !important' }} />}
                label="Built for focused minds"
                size="small"
                className="landing-hero__chip"
              />

              <Typography variant="h1" component="h1" className="landing-hero__headline">
                Think clearly.<br />
                Write freely.<br />
                <span className="landing-hero__gradient-text">Remember everything.</span>
              </Typography>

              <Typography variant="body1" className="landing-hero__subline">
                NoteCORE is the intelligent writing workspace that keeps your ideas
                organised, searchable, and always with you. Free forever.
              </Typography>

              <Box className="landing-hero__ctas">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => { trackHeroCTAClick(); navigate('/auth'); }}
                  sx={{
                    background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
                    borderRadius: '16px',
                    fontWeight: 700,
                    fontSize: 16,
                    textTransform: 'none',
                    px: 4,
                    py: 1.5,
                    boxShadow: '0 8px 32px rgba(124,58,237,0.48)',
                    '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #7c3aed)' },
                  }}
                >
                  Start writing — it&apos;s free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => { trackSeePricingClick(); scrollTo('pricing'); }}
                  sx={{
                    borderColor: 'rgba(167,139,250,0.48)',
                    color: 'rgba(255,255,255,0.72)',
                    borderRadius: '16px',
                    fontWeight: 600,
                    fontSize: 16,
                    textTransform: 'none',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: '#a78bfa',
                      color: '#fff',
                      backgroundColor: 'rgba(139,92,246,0.08)',
                    },
                  }}
                >
                  See pricing
                </Button>
              </Box>

              {/* Social proof */}
              {/* <Box className="landing-hero__testimonial">
                <FormatQuoteRoundedIcon sx={{ fontSize: 18, opacity: 0.38, mb: 0.5 }} />
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.62)', fontStyle: 'italic', lineHeight: 1.75 }}
                >
                  &ldquo;NoteCORE changed how I capture and act on ideas. It&apos;s the
                  cleanest writing tool I&apos;ve ever used.&rdquo;
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.32)', mt: 0.75, display: 'block' }}
                >
                  — Early beta user
                </Typography>
              </Box> */}
            </Box>
          </Container>

          {/* ── Features ──────────────────────────────────────────────────── */}
          <Container maxWidth="lg">
            <Box component="section" className="landing-section" id="features" aria-label="Features">
              <Typography variant="h2" component="h2" className="landing-section__eyebrow">
                <span className="landing-section__eyebrow-icon" aria-hidden="true">
                  <AutoAwesomeRoundedIcon sx={{ fontSize: 13 }} />
                </span>
                Everything you need
              </Typography>
              <Typography variant="h3" component="h3" className="landing-section__title">
                Built for the way you actually think
              </Typography>
              <Typography variant="body1" className="landing-section__subtitle">
                No subscriptions. No bloat. Just a fast, focused writing tool that gets out of your way.
              </Typography>

              <Grid container spacing={2.5} sx={{ mt: 4 }}>
                <FeatureCard
                  icon={<FlashOnRoundedIcon sx={{ fontSize: 22 }} />}
                  title="Instant rich-text editor"
                  description="Blocks, headings, lists, code, quotes — all without leaving the keyboard. Powered by Editor.js."
                />
                <FeatureCard
                  icon={<SyncRoundedIcon sx={{ fontSize: 22 }} />}
                  title="Syncs across every device"
                  description="Open on your phone, pick up on your laptop. Your notes are always in sync, instantly."
                />
                <FeatureCard
                  icon={<LockOutlinedIcon sx={{ fontSize: 22 }} />}
                  title="Private by default"
                  description="Your notes are yours. Zero ads, zero tracking, encrypted at rest and in transit."
                />
                <FeatureCard
                  icon={<BoltRoundedIcon sx={{ fontSize: 22 }} />}
                  title="Lightning fast"
                  description="No page refreshes. No loading spinners. NoteCORE responds instantly to every keystroke."
                />
                <FeatureCard
                  icon={<DevicesRoundedIcon sx={{ fontSize: 22 }} />}
                  title="Works everywhere"
                  description="Responsive from day one. Works beautifully on desktop, tablet, and mobile."
                />
                <FeatureCard
                  icon={<AutoAwesomeRoundedIcon sx={{ fontSize: 22 }} />}
                  title="Dark &amp; light mode"
                  description="Choose the theme that matches your vibe. Your preference is saved across all sessions."
                />
              </Grid>
            </Box>
          </Container>

          {/* ── Pricing ───────────────────────────────────────────────────── */}
          <Container maxWidth="md">
            <Box component="section" className="landing-section" id="pricing" aria-label="Pricing">
              <Typography variant="h2" component="h2" className="landing-section__eyebrow">
                <span className="landing-section__eyebrow-icon" aria-hidden="true">
                  <BoltRoundedIcon sx={{ fontSize: 13 }} />
                </span>
                Simple, honest pricing
              </Typography>
              <Typography variant="h3" component="h3" className="landing-section__title">
                One app. Two plans.
              </Typography>
              <Typography variant="body1" className="landing-section__subtitle">
                Start free forever. Upgrade once when you need more.
              </Typography>

              <Grid container spacing={2.5} justifyContent="center" sx={{ mt: 4 }}>

                {/* Basic */}
                <Grid item xs={12} sm={6}>
                  <Box className="landing-plan-card">
                    <Box className="landing-plan-card__header">
                      <Typography
                        variant="h6"
                        component="h4"
                        fontWeight={800}
                        sx={{ color: '#f0f0f0' }}
                      >
                        Basic
                      </Typography>
                      <span className="landing-plan-card__price">
                        <span className="landing-plan-card__price-amount">$0</span>
                        <span className="landing-plan-card__price-period"> / forever</span>
                      </span>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.5 }}>
                        No credit card required
                      </Typography>
                    </Box>

                    <Box className="landing-plan-card__features">
                      {[
                        'Up to 3 notes',
                        'Rich text block editor',
                        'All content block types',
                        'Google Sign-in',
                        'Dark & light mode',
                      ].map((f) => (
                        <Box key={f} className="landing-plan-card__feature">
                          <CheckRoundedIcon sx={{ fontSize: 16, color: '#8b5cf6', flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                            {f}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => { trackLandingPlanClick('basic'); navigate('/auth'); }}
                      sx={{
                        mt: 3,
                        borderColor: 'rgba(139,92,246,0.5)',
                        color: '#a78bfa',
                        borderRadius: '12px',
                        fontWeight: 700,
                        textTransform: 'none',
                        py: 1.25,
                        '&:hover': {
                          borderColor: '#8b5cf6',
                          backgroundColor: 'rgba(139,92,246,0.1)',
                        },
                      }}
                    >
                      Get started free
                    </Button>
                  </Box>
                </Grid>

                {/* Pro */}
                <Grid item xs={12} sm={6}>
                  <Box className="landing-plan-card landing-plan-card--pro">
                    <Box className="landing-plan-card__badge">
                      <WorkspacePremiumRoundedIcon sx={{ fontSize: 12 }} />
                      Most popular
                    </Box>

                    <Box className="landing-plan-card__header">
                      <Typography
                        variant="h6"
                        component="h4"
                        fontWeight={800}
                        sx={{ color: '#f0f0f0' }}
                      >
                        Pro
                      </Typography>
                      <span className="landing-plan-card__price">
                        <span className="landing-plan-card__price-amount">₹199</span>
                        <span className="landing-plan-card__price-period"> one-time</span>
                      </span>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.5 }}>
                        Pay once, yours forever
                      </Typography>
                    </Box>

                    <Box className="landing-plan-card__features">
                      {[
                        'Unlimited notes',
                        'Everything in Basic',
                        'Lifetime access',
                        'All future updates',
                        'One-time · Never pay again',
                      ].map((f) => (
                        <Box key={f} className="landing-plan-card__feature">
                          <AllInclusiveRoundedIcon sx={{ fontSize: 16, color: '#8b5cf6', flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                            {f}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => { trackLandingPlanClick('pro'); navigate('/auth'); }}
                      sx={{
                        mt: 3,
                        background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
                        borderRadius: '12px',
                        fontWeight: 700,
                        textTransform: 'none',
                        py: 1.25,
                        boxShadow: '0 4px 20px rgba(124,58,237,0.38)',
                        '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #7c3aed)' },
                      }}
                    >
                      Get Pro — ₹199
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Container>

          {/* ── FAQ ───────────────────────────────────────────────────────── */}
          <Container maxWidth="md">
            <Box component="section" className="landing-section" id="faq" aria-label="Frequently asked questions">
              <Typography variant="h2" component="h2" className="landing-section__eyebrow">
                FAQ
              </Typography>
              <Typography variant="h3" component="h3" className="landing-section__title">
                Common questions
              </Typography>

              <Box sx={{ mt: 4 }}>
                {FAQS.map((item, i) => (
                  <Accordion
                    key={i}
                    expanded={expandedFaq === i}
                    onChange={(_, isExpanded) => { if (isExpanded) trackFAQExpand(item.q); setExpandedFaq(isExpanded ? i : false); }}
                    disableGutters
                    elevation={0}
                    sx={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.07)',
                      borderRadius: '12px !important',
                      mb: 1.5,
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        background: 'rgba(139,92,246,0.08)',
                        border: '1px solid rgba(139,92,246,0.28)',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreRoundedIcon sx={{ color: 'rgba(255,255,255,0.45)' }} />}
                      sx={{ px: 2.5, py: 0.5 }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ color: 'rgba(255,255,255,0.88)' }}
                      >
                        {item.q}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.58)', lineHeight: 1.75 }}>
                        {item.a}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Box>
          </Container>

          {/* ── Final CTA ─────────────────────────────────────────────────── */}
          <Container maxWidth="md">
            <Box component="section" className="landing-cta" aria-label="Call to action">
              <Typography variant="h2" component="h2" className="landing-cta__title">
                Ready to think more clearly?
              </Typography>
              <Typography variant="body1" className="landing-cta__subtitle">
                Join writers, students, and founders who use NoteCORE every day.
                Start free — no credit card needed.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => { trackHeroCTAClick(); navigate('/auth'); }}
                sx={{
                  mt: 1,
                  background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
                  borderRadius: '16px',
                  fontWeight: 700,
                  fontSize: 16,
                  textTransform: 'none',
                  px: 5,
                  py: 1.75,
                  boxShadow: '0 8px 32px rgba(124,58,237,0.48)',
                  '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #7c3aed)' },
                }}
              >
                Start writing free
              </Button>
            </Box>
          </Container>
        </Box>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <Box component="footer" className="landing-footer">
          <Container maxWidth="lg">
            <Box className="landing-footer__inner">
              <Box>
                <LogoSVG height="26px" />
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.32)', mt: 1, fontSize: 13 }}
                >
                  The focused writing workspace.
                </Typography>
              </Box>

              <Box className="landing-footer__links">
                <Typography
                  variant="body2"
                  component="a"
                  href="/auth"
                  className="landing-footer__link"
                >
                  Sign in
                </Typography>
                <Typography
                  variant="body2"
                  component="button"
                  onClick={() => scrollTo('pricing')}
                  className="landing-footer__link"
                  sx={{ background: 'none', border: 'none', p: 0 }}
                >
                  Pricing
                </Typography>
                <Typography
                  variant="body2"
                  component="button"
                  onClick={() => scrollTo('faq')}
                  className="landing-footer__link"
                  sx={{ background: 'none', border: 'none', p: 0 }}
                >
                  FAQ
                </Typography>
                <Typography
                  variant="body2"
                  component={RouterLink}
                  to="/terms"
                  className="landing-footer__link"
                >
                  Terms of Service
                </Typography>
                <Typography
                  variant="body2"
                  component={RouterLink}
                  to="/privacy"
                  className="landing-footer__link"
                >
                  Privacy Policy
                </Typography>
              </Box>
            </Box>

            <Box className="landing-footer__bottom">
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.22)', fontSize: 12 }}>
                &copy; {new Date().getFullYear()} NoteCORE. All rights reserved.
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default LandingPage;
